import { useState } from 'react'
import { useParams } from 'react-router-dom'
import { Badge } from '../catalyst/badge'
import { Button } from '../catalyst/button'
import { Dialog, DialogActions, DialogBody, DialogDescription, DialogTitle } from '../catalyst/dialog'
import { Field as FormField, FieldGroup, Label } from '../catalyst/fieldset'
import { Subheading } from '../catalyst/heading'
import { Input } from '../catalyst/input'
import { Text } from '../catalyst/text'
import { Textarea } from '../catalyst/textarea'
import { useAuth } from '../lib/auth'
import { issueFor, useCanadaGapFarmStatus, useEntryCheck } from '../lib/canadagap'
import { BUILDING_CHECKLIST, BUILDING_SECTIONS, countAnswers, maintenanceSide } from '../lib/canadagap-checklists'
import { ChecklistFieldset, type ChecklistValue } from '../lib/checklist-fieldset'
import { ComplianceBadge } from '../lib/compliance-badge'
import { useBuildingInspections, type BuildingInspectionInput } from '../lib/farm-level-logs'
import { byDateDesc, FarmLogLayout, formatDate, orNull, text, useSavedEntries, type DialogState } from '../lib/farm-log-layout'
import { useLogNames } from '../lib/farm-log-names'
import { AssetNameInput } from '../lib/asset-picker'
import type { CanadaGapCheckResult } from '../lib/canadagap'
import { assetIdFor, useFarmAssets } from '../lib/inventory'
import type { Asset, BuildingInspection, Checklist } from '../lib/types'
import { checkBadgeProps, EntryCheckPanel, InputWarning } from './entry-check'
import { EntryIssues } from './entry-issues'

const FORM_NAME = 'Cleaning, Maintenance and Repair of Buildings'
type Side = 'interior' | 'exterior'

/** The four maintenance columns Form G has for each side. */
type Work = { needed: string; doneOn: string; doneBy: string; overseenBy: string }
const workOf = (log: BuildingInspection | undefined, side: Side): Work => ({
  needed: text(log?.[`${side}WorkNeeded`]),
  doneOn: text(log?.[`${side}WorkDoneOn`]),
  doneBy: text(log?.[`${side}WorkDoneBy`]),
  overseenBy: text(log?.[`${side}WorkOverseenBy`]),
})

function MaintenanceFields({
  side,
  value,
  onChange,
  check,
}: {
  side: Side
  value: Work
  onChange: (value: Work) => void
  check: CanadaGapCheckResult | null
}) {
  const set = (key: keyof Work) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => onChange({ ...value, [key]: e.target.value })
  return (
    <div className="rounded-lg p-4 ring-1 ring-zinc-950/10 dark:ring-white/10">
      <Subheading level={3} className="!text-sm">
        {side === 'interior' ? 'Interior' : 'Exterior'} maintenance required
      </Subheading>
      <FieldGroup className="mt-3">
        <FormField>
          <Label>Description of work needed</Label>
          <Textarea rows={2} value={value.needed} onChange={set('needed')} />
          <InputWarning result={check} input={`${side}WorkNeeded`} />
        </FormField>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <FormField>
            <Label>Date completed</Label>
            <Input type="date" value={value.doneOn} onChange={set('doneOn')} />
          </FormField>
          <FormField>
            <Label>Completed by</Label>
            <Input value={value.doneBy} onChange={set('doneBy')} />
            <InputWarning result={check} input={`${side}WorkDoneBy`} />
          </FormField>
          <FormField>
            <Label>Overseen by</Label>
            <Input value={value.overseenBy} onChange={set('overseenBy')} />
            <InputWarning result={check} input={`${side}WorkOverseenBy`} />
          </FormField>
        </div>
      </FieldGroup>
    </div>
  )
}

function BuildingInspectionDialog({
  onClose,
  initial,
  buildings,
  assets,
  onSave,
}: {
  onClose: () => void
  initial?: BuildingInspection
  buildings: string[]
  /** Inventory buildings at this farm. */
  assets: Asset[]
  onSave: (input: BuildingInspectionInput) => Promise<unknown>
}) {
  const [buildingName, setBuildingName] = useState(initial?.buildingName ?? '')
  const [inspectedOn, setInspectedOn] = useState(initial?.inspectedOn ?? '')
  const [completedBy, setCompletedBy] = useState(text(initial?.completedBy))
  const [checklist, setChecklist] = useState<ChecklistValue>(initial?.checklist ?? {})
  const [work, setWork] = useState<Record<Side, Work>>({ interior: workOf(initial, 'interior'), exterior: workOf(initial, 'exterior') })
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // A side's maintenance fields show once it has an Issue, or if work was already recorded.
  const hasIssue = (side: Side) => BUILDING_CHECKLIST.some((i) => maintenanceSide(i.section) === side && checklist[i.key] === 'not_ok')
  const showWork = (side: Side) => hasIssue(side) || Object.values(work[side]).some(Boolean)

  const sideInput = (side: Side) => ({
    [`${side}WorkNeeded`]: orNull(work[side].needed),
    [`${side}WorkDoneOn`]: work[side].doneOn || null,
    [`${side}WorkDoneBy`]: orNull(work[side].doneBy),
    [`${side}WorkOverseenBy`]: orNull(work[side].overseenBy),
  })
  const { org } = useAuth()
  const { farmId } = useParams<{ farmId: string }>()
  const assetId = assetIdFor(assets, buildingName, initial && { name: initial.buildingName, assetId: initial.assetId })
  const check = useEntryCheck(org?.id, farmId, { form: 'g', entry: { buildingName: orNull(buildingName), inspectedOn: inspectedOn || null, completedBy: orNull(completedBy), checklist, ...sideInput('interior'), ...sideInput('exterior') } })

  async function handleSubmit(event: React.FormEvent) {
    event.preventDefault()
    if (!buildingName.trim() || !inspectedOn) return
    setSubmitting(true)
    setError(null)
    try {
      await onSave({
        buildingName: buildingName.trim(),
        assetId,
        inspectedOn,
        completedBy: orNull(completedBy),
        checklist: checklist as Checklist,
        ...sideInput('interior'),
        ...sideInput('exterior'),
      })
      onClose()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save inspection.')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Dialog open onClose={onClose} size="3xl">
      <DialogTitle>{initial ? 'Edit building inspection' : 'Inspect a building'}</DialogTitle>
      <DialogDescription>Monthly inspection of the building interior and exterior. Mark N/A where a structure doesn't apply.</DialogDescription>
      <form onSubmit={handleSubmit}>
        <div className="mt-2 flex flex-wrap gap-1.5">
          <ComplianceBadge framework="CanadaGAP" formCode="G" formName={FORM_NAME} {...checkBadgeProps(check)} />
        </div>
        <DialogBody>
          {error && <Text className="mb-4 text-red-600 dark:text-red-400">{error}</Text>}
          <FieldGroup>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
              <FormField>
                <Label>Building ID # / name</Label>
                <AssetNameInput
                  assets={assets}
                  pastNames={buildings}
                  linkedAssetId={assetId}
                  inventoryHref={`/farms/${farmId}/inventory`}
                  value={buildingName}
                  onChange={setBuildingName}
                  placeholder="e.g. Packing shed"
                  required
                  autoFocus
                />
              </FormField>
              <FormField>
                <Label>Date</Label>
                <Input type="date" value={inspectedOn} onChange={(e) => setInspectedOn(e.target.value)} required />
              </FormField>
              <FormField>
                <Label>Completed by</Label>
                <Input value={completedBy} onChange={(e) => setCompletedBy(e.target.value)} />
                <InputWarning result={check} input="completedBy" />
              </FormField>
            </div>
          </FieldGroup>

          <div className="mt-8">
            <ChecklistFieldset sections={BUILDING_SECTIONS} items={BUILDING_CHECKLIST} value={checklist} onChange={setChecklist} />
          </div>

          {(['interior', 'exterior'] as const).some(showWork) && (
            <div className="mt-8 space-y-4">
              {(['interior', 'exterior'] as const)
                .filter(showWork)
                .map((side) => (
                  <MaintenanceFields key={side} side={side} value={work[side]} check={check} onChange={(value) => setWork((w) => ({ ...w, [side]: value }))} />
                ))}
            </div>
          )}
          <EntryCheckPanel result={check} formCode="G" />
        </DialogBody>
        <DialogActions>
          <div className="sm:mr-auto">
            <ComplianceBadge framework="CanadaGAP" formCode="G" formName={FORM_NAME} placement="above" {...checkBadgeProps(check)} />
          </div>
          <Button plain onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" disabled={submitting}>
            {submitting ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  )
}

function maintenanceLine(log: BuildingInspection, side: Side) {
  const w = workOf(log, side)
  if (!w.needed) return null
  return (
    <p className="mt-0.5 text-sm text-zinc-500 dark:text-zinc-400">
      <span className="capitalize">{side}</span>: {w.needed}
      {w.doneOn ? (
        <>
          {' '}
          — done {formatDate(w.doneOn)}
          {w.doneBy && <> by {w.doneBy}</>}
        </>
      ) : (
        <span className="text-amber-600 dark:text-amber-400"> — open</span>
      )}
    </p>
  )
}

export function BuildingInspectionsPage() {
  const { farmId } = useParams<{ farmId: string }>()
  const { org } = useAuth()
  const [year, setYear] = useState(new Date().getFullYear())
  const [building, setBuilding] = useState('')
  const [dialog, setDialog] = useState<DialogState<BuildingInspection>>(null)
  const saved = useSavedEntries(dialog !== null, year)

  const { logs, loading, error, addLog, updateLog } = useBuildingInspections(org?.id, farmId, year, building || undefined)
  const { status, reload: reloadStatus } = useCanadaGapFarmStatus(org?.id, farmId, year)
  const { names, reload: reloadNames } = useLogNames(org?.id, farmId, 'building')
  const { assets } = useFarmAssets(org?.id, farmId, 'BUILDING')

  async function save(input: BuildingInspectionInput) {
    const log = dialog?.log ? await updateLog(dialog.log.id, input) : await addLog(input)
    await Promise.all([reloadStatus(), reloadNames()])
    if (log) saved.setIds([log.id])
  }

  const entries = [...logs].sort(byDateDesc((r) => r.inspectedOn)).map((log) => {
    const counts = countAnswers(BUILDING_CHECKLIST, log.checklist)
    return {
      key: log.id,
      date: log.inspectedOn,
      onClick: () => setDialog({ log }),
      content: (
        <>
          <p className="mt-0.5 text-sm text-zinc-700 dark:text-zinc-300">
            {log.buildingName}
            {log.completedBy && <span className="text-zinc-400 dark:text-zinc-500"> · {log.completedBy}</span>}
            {log.assetId && (
              <Badge color="zinc" className="ml-2">
                Inventory
              </Badge>
            )}
          </p>
          <p className="mt-0.5 text-sm text-zinc-500 dark:text-zinc-400">
            {counts.ok}/{counts.total} OK
            {counts.issues > 0 && <span className="text-red-600 dark:text-red-400"> · {counts.issues} issue{counts.issues > 1 ? 's' : ''}</span>}
            {counts.answered < counts.total && <> · {counts.total - counts.answered} not answered</>}
          </p>
          {maintenanceLine(log, 'interior')}
          {maintenanceLine(log, 'exterior')}
          <EntryIssues issue={issueFor(status?.forms.g, log.id)} />
        </>
      ),
    }
  })

  return (
    <FarmLogLayout
      orgId={org?.id}
      farmId={farmId}
      form="g"
      title="Building inspections"
      formName={FORM_NAME}
      addLabel="Inspect a building"
      addHref={`/farms/${farmId}/log/building_inspection`}
      year={year}
      onYearChange={setYear}
      nameFilter={{ label: 'Building', options: names, value: building, onChange: setBuilding }}
      status={status}
      loading={loading}
      error={error}
      entries={entries}
      emptyText={`No building inspections in ${year}.`}
      saved={saved}
    >
      {dialog && (
        <BuildingInspectionDialog key={dialog.log?.id ?? 'new'} onClose={() => setDialog(null)} initial={dialog.log} buildings={names} assets={assets} onSave={save} />
      )}
    </FarmLogLayout>
  )
}
